/**
 * Coup de pouce — Attendu 1053 « écrire une fraction sous la forme d'une
 * somme d'un nombre entier et d'une fraction inférieure à 1 : 17/5 = 3 +
 * 2/5 ». UNE page STATIQUE partagée par les trois generator_keys. Écrite à
 * l'étape 5 de revue-gen contre la doctrine, validée par le prof le
 * 2026-09-08.
 *
 * L'image que l'élève a déjà : la demi-droite graduée du CM1 (« placer la
 * somme d'un nombre entier et d'une fraction inférieure à un sur une
 * demi-droite graduée ») avec les arcs de parts de 1050. C'est ici que vit
 * le « c'est aussi 1 + 2/3 » écarté de la fiche 1050. Cinq éléments, deux
 * couleurs, un mot par objet (unités, parts) :
 *   1. la demi-droite de 0 à 4, l'unité partagée en 5 parts, A à 17/5,
 *      jamais étiqueté ;
 *   2. en bleu, sous la droite, de 0 à 3 : « 3 unités entières » ;
 *   3. en orange, deux arcs de 3 jusqu'à A, numérotés 1 et 2 : « 2 parts
 *      qui restent » ;
 *   4. en gros : 17/5 = 3 + 2/5, le 3 en bleu, le 2/5 en orange ;
 *   5. la règle, cause puis effet : « A est après 3 et avant 4 : on a 3
 *      unités entières. » « Après 3, il reste 2 parts sur les 5 d'une
 *      unité : 2/5. » (le dénominateur est le nombre de parts d'une unité,
 *      dit en clair, relecture prof 2026-09-09) et la vérification
 *      « Je vérifie : 15 parts dans 3 unités, plus 2 parts : 17. »
 * Rejetés : la barre partagée de 1049 (un partage, pas « combien d'unités
 * entières ») et toute ligne « 17 = 3 × 5 + 2 » (langue de division).
 * Mots interdits : quotient, reste, division.
 *
 * SACRIFIÉ : 17/5 sous toutes ses formes ne sort d'aucune carte (gen_1053,
 * auto-testé par cdp1053.self.test.js). Largeur bornée à 340 px, tailles
 * de 1048.
 */

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';

/** La demi-droite : 0 → 4 sur 320 px, cinquièmes. */
const X0 = 40;
const UNITE = 80;
const DEN = 5;
const PART = UNITE / DEN;
const Y = 92;
const toX = (k) => X0 + k * PART;
const A = 17;

/** Graduations : 0 à 4 en cinquièmes, entiers plus longs et étiquetés. */
const GRADUATIONS = Array.from({ length: 4 * DEN + 1 }, (_, k) => {
    const x = toX(k);
    const entier = k % DEN === 0;
    const h = entier ? 9 : 5;
    return `<line x1="${x}" y1="${Y - h}" x2="${x}" y2="${Y + h}" stroke="${ENCRE}" stroke-width="${entier ? 2 : 1.2}"/>`
        + (entier ? `<text x="${x}" y="${Y + 24}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ENCRE}">${k / DEN}</text>` : '');
}).join('\n  ');

/** Les deux arcs orange de 3 jusqu'à A, numérotés. */
const ARCS = Array.from({ length: A - 3 * DEN }, (_, k) => {
    const x1 = toX(3 * DEN + k);
    const x2 = toX(3 * DEN + k + 1);
    const xm = (x1 + x2) / 2;
    return `<path d="M ${x1} ${Y - 10} Q ${xm} ${Y - 30} ${x2} ${Y - 10}" fill="none" stroke="${ORANGE}" stroke-width="1.8"/>
  <text x="${xm}" y="${Y - 27}" font-size="9" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${k + 1}</text>`;
}).join('\n  ');

/** Une fraction empilée, une couleur. */
const frac = (x, y, n, d, couleur, taille = 17, demi = 12) => `<text x="${x}" y="${y - 5}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - demi}" y1="${y}" x2="${x + demi}" y2="${y}" stroke="${couleur}" stroke-width="1.8"/>
  <text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

const SVG_ENTIER = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="Demi-droite graduée de 0 à 4, l'unité partagée en 5 parts, un point A entre 3 et 4. Sous la droite, en bleu, de 0 à 3 : 3 unités entières. Au-dessus, en orange, deux arcs de 3 jusqu'à A : 2 parts qui restent. 17 sur 5 égale 3 plus 2 sur 5. A est après 3 et avant 4 : on a 3 unités entières. Après 3, il reste 2 parts sur les 5 d'une unité : 2 sur 5. Je vérifie : 15 parts dans 3 unités, plus 2 parts : 17.">
  <!-- 1. la demi-droite, A à 17/5 -->
  <line x1="${X0 - 10}" y1="${Y}" x2="${toX(4 * DEN) + 22}" y2="${Y}" stroke="${ENCRE}" stroke-width="2"/>
  <polygon points="${toX(4 * DEN) + 30},${Y} ${toX(4 * DEN) + 20},${Y - 5} ${toX(4 * DEN) + 20},${Y + 5}" fill="${ENCRE}"/>
  ${GRADUATIONS}
  <circle cx="${toX(A)}" cy="${Y}" r="5" fill="${ORANGE}"/>
  <text x="${toX(A)}" y="${Y + 24}" font-size="12" font-weight="bold" text-anchor="middle" fill="${ORANGE}">A</text>

  <!-- 2. en bleu, de 0 à 3 : 3 unités entières -->
  <line x1="${toX(0)}" y1="${Y + 38}" x2="${toX(3 * DEN)}" y2="${Y + 38}" stroke="${BLEU}" stroke-width="2"/>
  <line x1="${toX(0)}" y1="${Y + 34}" x2="${toX(0)}" y2="${Y + 42}" stroke="${BLEU}" stroke-width="2"/>
  <line x1="${toX(3 * DEN)}" y1="${Y + 34}" x2="${toX(3 * DEN)}" y2="${Y + 42}" stroke="${BLEU}" stroke-width="2"/>
  <text x="${toX(1.5 * DEN)}" y="${Y + 56}" font-size="12" font-weight="bold" text-anchor="middle" fill="${BLEU}">3 unités entières</text>

  <!-- 3. en orange, deux arcs de 3 à A : 2 parts qui restent -->
  ${ARCS}
  <text x="${toX(3 * DEN + 1)}" y="${Y - 42}" font-size="11" font-weight="bold" text-anchor="middle" fill="${ORANGE}">2 parts qui restent</text>

  <!-- 4. le résultat -->
  ${frac(118, 208, 17, 5, ENCRE)}
  <text x="152" y="214" font-size="17" font-weight="bold" text-anchor="middle" fill="${ENCRE}">=</text>
  <text x="178" y="214" font-size="17" font-weight="bold" text-anchor="middle" fill="${BLEU}">3</text>
  <text x="200" y="214" font-size="17" font-weight="bold" text-anchor="middle" fill="${ENCRE}">+</text>
  ${frac(226, 208, 2, 5, ORANGE)}

  <!-- 5. la règle, cause puis effet, et la vérification -->
  <text x="200" y="250" font-size="12" text-anchor="middle" fill="${ENCRE}">A est après 3 et avant 4 : on a <tspan font-weight="bold" fill="${BLEU}">3 unités entières</tspan>.</text>
  <text x="200" y="268" font-size="12" text-anchor="middle" fill="${ENCRE}">Après 3, il reste <tspan font-weight="bold" fill="${ORANGE}">2 parts</tspan> sur les 5 d'une unité : <tspan font-weight="bold" fill="${ORANGE}">2/5</tspan>.</text>
  <text x="200" y="290" font-size="11.5" text-anchor="middle" fill="${ENCRE}">Je vérifie : 15 parts dans 3 unités, plus 2 parts : 17.</text>
</svg>`.trim();

const AIDE = {
    pages: [
        { titre: 'L\'entier passé, et les parts qui restent', svg: SVG_ENTIER },
    ],
};

const CP_1053 = {
    flash_texte_trou_saisie: AIDE,
    entier_qcm_unique: AIDE,
    entier_tableau_vf: AIDE,
};

export default CP_1053;
